import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useMemo, useRef } from "react";
import type { AnimationEffectType, Intent } from "#/_packages/umbrella/types";
import {
	normalizeAnimationEffectType,
	toAnimationEffectCssClass,
	toAnimationEffectStyle,
} from "../utils/animation-utils.ts";

type MagneticMotionState = {
	element: HTMLElement | null;
	currentX: number;
	currentY: number;
	targetX: number;
	targetY: number;
	raf: number;
};

export type AnimationEffectProps = {
	className: string | null;
	style: CSSProperties | undefined;
	"data-anim-effect": AnimationEffectType;
	onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
};

type UseAnimationEffectOptions = {
	animationEffectType?: AnimationEffectType | string | null;
	intentColor?: Intent;
};

function prefersReducedMotion(): boolean {
	return (
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	);
}

function getMagneticStrength(element: HTMLElement): number {
	const raw = getComputedStyle(element)
		.getPropertyValue("--umb-magnetic-strength")
		.trim();
	const parsed = Number.parseFloat(raw);
	return Number.isFinite(parsed) ? parsed : 0.25;
}

function resetMagneticMotion(state: MagneticMotionState): void {
	if (state.raf) cancelAnimationFrame(state.raf);
	state.raf = 0;
	state.currentX = state.currentY = state.targetX = state.targetY = 0;
	state.element?.classList.remove("is-magnetic-active");
	state.element?.style.setProperty("--umb-magnetic-translate-x", "0px");
	state.element?.style.setProperty("--umb-magnetic-translate-y", "0px");
}

export function useAnimationEffect({
	animationEffectType,
	intentColor = "none",
}: UseAnimationEffectOptions): AnimationEffectProps {
	const motionState = useRef<MagneticMotionState>({
		element: null,
		currentX: 0,
		currentY: 0,
		targetX: 0,
		targetY: 0,
		raf: 0,
	});

	const normalizedAnimationEffectType =
		normalizeAnimationEffectType(animationEffectType);
	const className = toAnimationEffectCssClass(normalizedAnimationEffectType);
	const style = useMemo(
		() => toAnimationEffectStyle(normalizedAnimationEffectType, intentColor),
		[normalizedAnimationEffectType, intentColor],
	);

	useEffect(() => {
		if (normalizedAnimationEffectType !== "magnetic") return;
		const state = motionState.current;
		const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
		const suspend = () => {
			if (preference.matches || document.hidden) resetMagneticMotion(state);
		};
		preference.addEventListener("change", suspend);
		document.addEventListener("visibilitychange", suspend);
		suspend();
		return () => {
			resetMagneticMotion(state);
			preference.removeEventListener("change", suspend);
			document.removeEventListener("visibilitychange", suspend);
		};
	}, [normalizedAnimationEffectType]);

	const scheduleTransformUpdate = (
		element: HTMLElement,
		targetX: number,
		targetY: number,
	): void => {
		const easing = 0.18;
		const state = motionState.current;
		state.element = element;
		state.targetX = targetX;
		state.targetY = targetY;

		if (state.raf) return;

		const animate = (): void => {
			const deltaX = state.targetX - state.currentX;
			const deltaY = state.targetY - state.currentY;

			state.currentX += deltaX * easing;
			state.currentY += deltaY * easing;

			element.style.setProperty(
				"--umb-magnetic-translate-x",
				`${state.currentX}px`,
			);
			element.style.setProperty(
				"--umb-magnetic-translate-y",
				`${state.currentY}px`,
			);

			const nearTarget = Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5;

			if (!nearTarget) {
				state.raf = requestAnimationFrame(animate);
				return;
			}

			state.currentX = state.targetX;
			state.currentY = state.targetY;
			element.style.setProperty(
				"--umb-magnetic-translate-x",
				`${state.currentX}px`,
			);
			element.style.setProperty(
				"--umb-magnetic-translate-y",
				`${state.currentY}px`,
			);
			state.raf = 0;
		};

		state.raf = requestAnimationFrame(animate);
	};

	const handlePointerEnter = (event: ReactPointerEvent<HTMLElement>): void => {
		const element = event.currentTarget;

		if (normalizedAnimationEffectType === "ripplewave") {
			const rect = element.getBoundingClientRect();
			const pointerX = event.clientX - rect.left;
			const pointerY = event.clientY - rect.top;
			const rippleSize = Math.max(rect.width, rect.height) * 3;
			const rippleRadius = rippleSize * 0.5;
			const durationSeconds = Math.min(
				1.4,
				Math.max(0.45, rippleRadius / 1100),
			);

			element.classList.add("ambient-ripple-ready");
			element.style.setProperty("--umb-ripple-x", `${pointerX}px`);
			element.style.setProperty("--umb-ripple-y", `${pointerY}px`);
			element.style.setProperty("--umb-ripple-size", `${rippleSize}px`);
			element.style.setProperty("--umb-ripple-duration", `${durationSeconds}s`);

			element.classList.remove("is-rippling");
			void element.offsetWidth;
			element.classList.add("is-rippling");
			return;
		}

		if (normalizedAnimationEffectType === "magnetic") {
			motionState.current.element = element;
			if (prefersReducedMotion() || document.hidden) {
				resetMagneticMotion(motionState.current);
			} else {
				element.classList.add("is-magnetic-active");
			}
		}
	};

	const handlePointerLeave = (event: ReactPointerEvent<HTMLElement>): void => {
		const element = event.currentTarget;

		if (normalizedAnimationEffectType === "ripplewave") {
			element.classList.remove("is-rippling");
			return;
		}

		if (normalizedAnimationEffectType === "magnetic") {
			element.classList.remove("is-magnetic-active");
			motionState.current.element = element;
			if (prefersReducedMotion() || document.hidden) {
				resetMagneticMotion(motionState.current);
			} else {
				scheduleTransformUpdate(element, 0, 0);
			}
		}
	};

	const handlePointerMove = (event: ReactPointerEvent<HTMLElement>): void => {
		if (
			normalizedAnimationEffectType !== "magnetic" ||
			prefersReducedMotion() ||
			document.hidden
		) {
			return;
		}

		const element = event.currentTarget;
		if (!element.classList.contains("is-magnetic-active")) return;

		const rect = element.getBoundingClientRect();
		const offsetX = event.clientX - rect.left - rect.width / 2;
		const offsetY = event.clientY - rect.top - rect.height / 2;
		const strength = getMagneticStrength(element);
		scheduleTransformUpdate(element, offsetX * strength, offsetY * strength);
	};

	return {
		className,
		style,
		"data-anim-effect": normalizedAnimationEffectType,
		onPointerEnter: handlePointerEnter,
		onPointerLeave: handlePointerLeave,
		onPointerMove: handlePointerMove,
	};
}
